import React, { useEffect } from "react";
import { Link} from "react-router-dom";
import "aos/dist/aos.css";
import { Gift, ShieldCheck, Smartphone, Target, UserX, Zap } from "lucide-react";
import { Helmet } from "react-helmet";

const FreeGames = () => {


  useEffect(() => {
  import("aos").then((AOS) => {
    AOS.default.init({
      duration: 500,
      once: true,
      offset: 80,
      easing: "ease-out-cubic",
    });
  });
}, []);

const featuresList = [
  { title: "100% Free Forever", desc: "No hidden costs, no premium subscriptions. Every game is completely free online." },
  { title: "Instant Load Times", desc: "Optimized servers ensure our browser games start instantly with zero waiting." },
  { title: "Huge Variety", desc: "Racing, puzzles, action, and casual games — play free online games for everyone." },
  { title: "Safe & Secure", desc: "Daily malware scans ensure a secure online games experience." },
  { title: "No Account Needed", desc: "Play free online instantly without signup or personal data." },
  { title: "Mobile Friendly", desc: "Fully responsive free games on phones, tablets & desktops." }
];

const schemaData = {
  "@context": "https://schema.org",
  "@type": "ItemList",
  "name": "Why Play Free Games at Game Zone Hub?",
  "itemListElement": featuresList.map((feature, index) => ({
    "@type": "ListItem",
    "position": index + 1,
    "name": feature.title,
    "description": feature.desc
  }))
};


  return (
    <section 
      className="relative bg-gradient-to-b lg:px-8 from-[#0b1120] to-[#141a33] pt-[50px] text-white pb-10"
    >
      <Helmet>
        {schemaData && (
          <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }} />
        )}
      </Helmet>
      <div className="container mx-auto px-4 text-center">

        <h2 
          className="text-2xl sm:text-3xl md:text-4xl xl:text-5xl font-extrabold mb-6"
          data-aos="fade-down"
          data-aos-delay="200"
        >
          Why <span className="bg-clip-text text-transparent bg-gradient-to-r from-cyan-400 via-blue-400 to-purple-300">Free Games</span> at Game Zone Hub ?
        </h2>

        <p 
          className="sm:text-lg text-[16px] text-gray-300 max-w-4xl mx-auto leading-relaxed sm:mb-16 mb-10"
          data-aos="fade-up"
          data-aos-delay="400"
        >
          We are redefining the way you <strong>play online games</strong>. 
          Forget about annoying paywalls, lengthy installations, and unsafe downloads. 
          <strong>Game Zone Hub</strong> offers a premium, lag-free gaming experience directly in your browser. 
          Enjoy a safe, secure, and <strong>100% free</strong> environment to <strong>play free online games</strong> endlessly.
        </p>

        <ul className="grid lg:grid-cols-3 md:grid-cols-2 sm:grid-cols-1 gap-6 max-w-6xl mx-auto">
        
        <li data-aos="zoom-in-up" data-aos-delay="100">
          <div className="group bg-[#1e293b] h-full p-8 rounded-2xl border border-white/5 
            transition-all duration-300 hover:-translate-y-2 hover:scale-[1.03]
            hover:shadow-[0_0_30px_rgba(59,130,246,0.35)] hover:border-blue-500/60">
            <Gift className="w-12 h-12 text-blue-400 mx-auto mb-4 group-hover:scale-110 transition" />
            <h3 className="text-xl font-bold text-white mb-2 text-center">
              {featuresList[0].title}
            </h3>
            <p className="text-gray-400 text-sm text-center leading-6">
              {featuresList[0].desc}
            </p>
          </div>
        </li>

        <li data-aos="zoom-in-up" data-aos-delay="200">
          <div className="group bg-[#1e293b] h-full p-8 rounded-2xl border border-white/5 
            transition-all duration-300 hover:-translate-y-2 hover:scale-[1.03]
            hover:shadow-[0_0_30px_rgba(234,179,8,0.35)] hover:border-yellow-500/60">
            <Zap className="w-12 h-12 text-yellow-400 mx-auto mb-4 group-hover:scale-110 transition" />
            <h3 className="text-xl font-bold text-white mb-2 text-center">
              {featuresList[1].title}
            </h3>
            <p className="text-gray-400 text-sm text-center leading-6">
              {featuresList[1].desc}
            </p>
          </div>
        </li>

        <li data-aos="zoom-in-up" data-aos-delay="300">
          <div className="group bg-[#1e293b] h-full p-8 rounded-2xl border border-white/5 
            transition-all duration-300 hover:-translate-y-2 hover:scale-[1.03]
            hover:shadow-[0_0_30px_rgba(236,72,153,0.35)] hover:border-pink-500/60">
            <Target className="w-12 h-12 text-pink-400 mx-auto mb-4 group-hover:scale-110 transition" />
            <h3 className="text-xl font-bold text-white mb-2 text-center">
              {featuresList[2].title}
            </h3>
            <p className="text-gray-400 text-sm text-center leading-6">
              {featuresList[2].desc}
            </p>
          </div>
        </li>

        <li data-aos="zoom-in-up" data-aos-delay="400">
          <div className="group bg-[#1e293b] h-full p-8 rounded-2xl border border-white/5 
            transition-all duration-300 hover:-translate-y-2 hover:scale-[1.03]
            hover:shadow-[0_0_30px_rgba(34,197,94,0.35)] hover:border-green-500/60">
            <ShieldCheck className="w-12 h-12 text-green-400 mx-auto mb-4 group-hover:scale-110 transition" />
            <h3 className="text-xl font-bold text-white mb-2 text-center">
              {featuresList[3].title}
            </h3>
            <p className="text-gray-400 text-sm text-center leading-6">
             {featuresList[3].desc}
            </p>
          </div>
        </li>

        <li data-aos="zoom-in-up" data-aos-delay="500">
          <div className="group bg-[#1e293b] h-full p-8 rounded-2xl border border-white/5 
            transition-all duration-300 hover:-translate-y-2 hover:scale-[1.03]
            hover:shadow-[0_0_30px_rgba(168,85,247,0.35)] hover:border-purple-500/60">
            <UserX className="w-12 h-12 text-purple-400 mx-auto mb-4 group-hover:scale-110 transition" />
            <h3 className="text-xl font-bold text-white mb-2 text-center">
              {featuresList[4].title}
            </h3>
            <p className="text-gray-400 text-sm text-center leading-6">
              {featuresList[4].desc}
            </p>
          </div>
        </li>

        <li data-aos="zoom-in-up" data-aos-delay="600">
          <div className="group bg-[#1e293b] h-full p-8 rounded-2xl border border-white/5 
            transition-all duration-300 hover:-translate-y-2 hover:scale-[1.03]
            hover:shadow-[0_0_30px_rgba(34,211,238,0.35)] hover:border-cyan-500/60">
            <Smartphone className="w-12 h-12 text-cyan-400 mx-auto mb-4 group-hover:scale-110 transition" />
            <h3 className="text-xl font-bold text-white mb-2 text-center">
              {featuresList[5].title}
            </h3>
            <p className="text-gray-400 text-sm text-center leading-6">
              {featuresList[5].desc}
            </p>
          </div>
        </li>

      </ul>

        <div 
          className="sm:mt-16 mt-10"
          data-aos="fade-up"
          data-aos-delay="500"
        >
          <Link 
            to="/games" 
            aria-label="Play Free Online Games"
            className="bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 sm:px-10 px-5 sm:py-4 py-3 rounded-full sm:text-lg text-[16px] font-semibold shadow-lg transition"
          >
            Play Free Online Games Now
          </Link>
        </div>
      </div>
    </section>
  );
};

export default FreeGames;
